import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    IconButton,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
} from "@mui/material";
import { useAlert } from "../alerts/AlertContext";
import CloseIcon from "@mui/icons-material/Close";
import AxiosInstance from "../../services/AxiosInstance";
import { ThemeProvider } from "../v2/home/theme/ThemeProvider";
import { ASSET_ASSIGNMENT_TYPES_URL } from "../../Constants/Constants";

const AssignmentTypeForm = ({
    open,
    onClose,
    editData = null,
    onSaved
}) => {

    const [formData, setFormData] = useState({
        code: "",
        name: "",
        enabled: true,
        description: ""
    });
    const showAlert = useAlert();
    const [saving, setSaving] = useState(false);

    useEffect(() => {

        if (editData) {

            setFormData({
                code: editData.code || "",
                name: editData.name || "",
                enabled: editData.enabled ?? true,
                description: editData.description || ""
            });

        } else {

            setFormData({
                code: "",
                name: "",
                enabled: true,
                description: ""
            });
        }

    }, [editData, open]);

    const handleCodeChange = (event) => {

        const { name, value } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleChange = (event) => {

        const { name, value } = event.target;

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEnabledChange = (event) => {

        setFormData(prev => ({
            ...prev,
            enabled: event.target.value
        }));
    };

    const validate = () => {

        if (!formData.name.trim()) {
            showAlert("Assignment Type name is required", 'error');
            return false;
        }



        return true;
    };

    const handleSubmit = async () => {

        if (!validate()) {
            return;
        }

        try {

            setSaving(true);

            const payload = {
                code: formData.code.trim(),
                name: formData.name.trim(),
                enabled: formData.enabled,
                description: formData.description.trim()
            };

            let response;

            if (editData?.id) {

                response = await AxiosInstance.put(
                    `${ASSET_ASSIGNMENT_TYPES_URL}/${editData.id}`,
                    payload
                );

            } else {

                response = await AxiosInstance.post(
                    ASSET_ASSIGNMENT_TYPES_URL,
                    payload
                );
            }
            showAlert(editData
                ? "Asset Assignment Type updated successfully"
                : "Asset Assignment Type created successfully", 'success');


            onSaved?.(response.data);

            onClose();

        } catch (error) {

            console.error(error);

            showAlert(
                error?.response?.data?.message ||
                "Unable to save Asset Assignment Type", 'error');


        } finally {

            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >

            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between"
                }}
            >
                {editData
                    ? "Edit Asset Assignment"
                    : "Add Asset Assignment"}

                <IconButton
                    onClick={onClose}
                    disabled={saving}
                >
                    <CloseIcon />
                </IconButton>

            </DialogTitle>

            <DialogContent dividers>
                <ThemeProvider>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Code"
                            name="code"
                            value={formData.code}
                            onChange={handleCodeChange}
                            required
                            autoFocus
                        />

                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            pt: 1
                        }}
                    >

                        <TextField
                            fullWidth
                            label="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            autoFocus
                        />

                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2
                            }}
                        >
                            <FormControl>
                                <FormLabel id="demo-row-radio-buttons-group-label">
                                    Enabled
                                </FormLabel>
                                <RadioGroup
                                    row
                                    aria-labelledby="demo-row-radio-buttons-group-label"
                                    name="row-radio-buttons-group"
                                    value={formData.enabled}
                                    onChange={handleEnabledChange}
                                >
                                    <FormControlLabel
                                        value={"true"}
                                        control={<Radio />}
                                        label="Yes"
                                    />
                                    <FormControlLabel
                                        value={"false"}
                                        control={<Radio />}
                                        label="No"
                                    />
                                </RadioGroup>
                            </FormControl>

                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                        />

                    </Box>
                </ThemeProvider>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>

                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    Cancel
                </Button>

                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving}
                >
                    {saving
                        ? "Saving..."
                        : editData
                            ? "Update"
                            : "Save"}
                </Button>

            </DialogActions>

        </Dialog>
    );
};

export default AssignmentTypeForm;